'use client'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import React from 'react'

import { BlogArticleCard, getBlogArticleCardProps } from '@/components/Blog/BlogArticleCard'
import { containerClassName } from '@/components/Container'
import { Button } from '@/components/ui/button'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import type { BlogPostData } from '@/lib/blog/postCard'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { useTemplateHref, useTemplatePathMap } from '@/lib/pages/LocalizedPagePathsProvider'
import { getArchiveItemHrefFromMap } from '@/lib/pages/pagePaths'
import { cn } from '@/utilities/ui'

export type { BlogPostData } from '@/lib/blog/postCard'

type BlogSectionProps = {
  posts?: BlogPostData[]
}

export const BlogSection: React.FC<BlogSectionProps> = ({ posts = [] }) => {
  const locale = useLocale()
  const messages = useMessages()
  const copy = messages.landing.blog
  const postsHref = useTemplateHref('posts')
  const templatePaths = useTemplatePathMap()

  if (posts.length === 0) {
    return null
  }

  return (
    <section aria-labelledby="blog-section-heading" className="bg-cream">
      <div className={cn(containerClassName, 'py-12 lg:py-20')}>
        <div className="mb-10 flex flex-col gap-6 lg:mb-12 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
          <div className="min-w-0 max-w-4xl">
            <p className="font-outfit font-medium text-base text-bluetext">{copy.eyebrow}</p>
            <ColorTrailText
              as="h2"
              className="mt-3 text-balance break-words hyphens-auto text-title font-semibold leading-none text-bluetext"
              id="blog-section-heading"
              segments={[
                {
                  className: 'block',
                  finalColor: 'var(--brand-bluetext)',
                  text: copy.titleLine1,
                  trail: false,
                },
                {
                  className: 'block',
                  finalColor: 'var(--brand-dark-green)',
                  text: copy.titleLine2,
                },
              ]}
              wrapWords
            />
          </div>

          <Button
            asChild
            className="w-fit shrink-0"
            icon={<ArrowRight aria-hidden className="size-4" />}
          >
            <Link href={postsHref}>{copy.cta.label}</Link>
          </Button>
        </div>

        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          {posts.map((post, index) => (
            <BlogArticleCard
              key={post.id}
              {...getBlogArticleCardProps(
                post,
                getArchiveItemHrefFromMap('posts', post.slug, locale, templatePaths),
                copy.readTime,
                { index },
              )}
            />
          ))}
        </ul>
      </div>
    </section>
  )
}
